// src/components/pedigree/CanRiskModal.jsx

import React, { useState, useEffect, useContext } from 'react';
import axios from 'src/axios';
import { APICONSTANTS } from 'src/components/Constants';
import {
  Modal,
  Box,
  Typography,
  Grid,
  RadioGroup,
  FormControlLabel,
  Radio,
  TextField,
  Button,
  IconButton,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

import { ThemeContext, SnackContext } from 'src/store/ContextStore';

// ------------------------------
// Static look-up tables
// ------------------------------
const receptors = ['ER', 'PR', 'HER2', 'CK14', 'CK56'];

const pageCache = new Map(); // key = userId, value = { pages, fetchedAt }

const cancerSites = [
  { key: 'BC1', label: 'First breast cancer' },
  { key: 'BC2', label: 'Second breast cancer' },
  { key: 'OC', label: 'Ovarian cancer' },
  { key: 'PRO', label: 'Prostate cancer' },
  { key: 'PAN', label: 'Pancreatic cancer' }
];

// Genes covered by the CanRisk specification (v3)
const genes = [
  'BRCA1',
  'BRCA2',
  'PALB2',
  'ATM',
  'CHEK2',
  'BARD1',
  'RAD51D',
  'RAD51C',
  'BRIP1'
];

export default function CanRiskModal({
  open,
  onClose,
  onDownload,
  userId,
  data
}) {
  const { theme } = useContext(ThemeContext);
  const { setSnack } = useContext(SnackContext);

  // pages holds an array of member options
  const [pages, setPages] = useState([]);
  const [pageIdx, setPageIdx] = useState(0);
  const [jumpUPN, setJumpUPN] = useState('');
  const [canRiskConfigured, setCanRiskConfigured] = useState(false);
  const [canRiskChecking, setCanRiskChecking] = useState(false);
  const [calculating, setCalculating] = useState(false);
  const [scoreResult, setScoreResult] = useState(null);
  const [scoreError, setScoreError] = useState('');

  useEffect(() => {
    if (!open || !userId) return;

    const targetId =
      data?.data?.familyMemberId || data?.familyMemberId || 'proband';

    if (pageCache.has(userId)) {
      const cached = pageCache.get(userId);
      setPages(cached.pages);
      const idx = cached.pages.findIndex((p) => p.familyMemberId === targetId);
      setPageIdx(idx !== -1 ? idx : 0);
    }

    const src = axios
      .get(
        `${APICONSTANTS.fillCanRiskModal.replace(
          ':user_id',
          userId
        )}?targetId=${targetId}`
      )
      .then(({ data: resp }) => {
        const same = pageCache.get(userId)?.pages === resp.pages;
        if (!same) {
          setPages(resp.pages || []);
          setPageIdx(resp.targetIndex ?? 0);
        }

        pageCache.set(userId, { pages: resp.pages, fetchedAt: Date.now() });
      })
      .catch(() => {
        if (!pageCache.has(userId)) setPages([]);
      });

    return () => {
      src?.abort?.();
    };
  }, [open, userId, data]);

  useEffect(() => {
    if (!open) return;
    setCanRiskChecking(true);
    setCanRiskConfigured(false);
    axios
      .get(`${APICONSTANTS.canRiskSettings}?validate=true`)
      .then((res) => {
        setCanRiskConfigured(
          Boolean(res.data?.configured && res.data?.authenticated)
        );
      })
      .catch(() => setCanRiskConfigured(false))
      .finally(() => setCanRiskChecking(false));
  }, [open]);

  useEffect(() => {
    if (open) return;
    setScoreResult(null);
    setScoreError('');
    setCalculating(false);
  }, [open]);

  // guard against no data
  if (!open) return null;
  if (!Array.isArray(pages) || pages.length === 0) {
    return (
      <Modal open={open} onClose={onClose}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 400,
            p: 4,
            bgcolor:
              theme.appearance.value === 'light'
                ? 'white'
                : theme.appearance.shade,
            borderRadius: 2,
            textAlign: 'center'
          }}
        >
          <Typography>Loading CanRisk options…</Typography>
        </Box>
      </Modal>
    );
  }

  const cur = pages[pageIdx] || {};

  // update current member's settings
  const updateCurrent = (patch) =>
    setPages(pages.map((p, i) => (i === pageIdx ? { ...p, ...patch } : p)));

  const individualDataRows = (() => {
    const d = cur.individualData || {};
    const rows = [];
    const numLabels = {
      menarche: 'Menarche (age)',
      parity: 'Parity',
      ageFirstLiveBirth: 'Age at first live birth',
      bmi: 'BMI',
      alcoholGramsPerDay: 'Daily alcohol (g/day)',
      birads: 'BI-RADS',
      height: 'Height (cm)',
      weight: 'Weight (kg)',
      mammographicDensityVolpara: 'Density %Volpara',
      mammographicDensityVAS: 'Density %VAS'
    };
    Object.entries(numLabels).forEach(([k, label]) => {
      const f = d[k];
      if (f && !f.unknown && f.value !== '' && f.value != null) {
        rows.push([label, f.value]);
      }
    });
    const auxLabels = {
      ocUse: ['OC use', 'years'],
      mhtUse: ['MHT use', 'years'],
      menopause: ['Menopause status', 'age'],
      breastFeeding: ['Breastfeeding', 'months'],
      biopsy: ['Biopsy history', 'count']
    };
    Object.entries(auxLabels).forEach(([k, [label, auxKey]]) => {
      const f = d[k];
      if (f && f.status && f.status !== 'Unknown') {
        const aux = f[auxKey];
        rows.push([label, aux ? `${f.status} (${aux})` : f.status]);
      }
    });
    const yesNoLabels = {
      tubalLigation: 'Tubal ligation',
      endometriosis: 'Endometriosis',
      smoker: 'Smoker'
    };
    Object.entries(yesNoLabels).forEach(([k, label]) => {
      if (d[k] && d[k] !== 'Unknown') {
        rows.push([label, d[k]]);
      }
    });
    const prsLabels = {
      prsBreast: 'PRS — Breast',
      prsOvarian: 'PRS — Ovarian',
      prsProstate: 'PRS — Prostate'
    };
    Object.entries(prsLabels).forEach(([k, label]) => {
      const f = d[k];
      if (f && !f.unknown && (f.alpha || f.zscore)) {
        rows.push([label, `α=${f.alpha ?? '—'}, z=${f.zscore ?? '—'}`]);
      }
    });
    return rows;
  })();

  // ---------------------------------------
  //      Helper renderers
  // ---------------------------------------
  // ------------------------------
  // Receptor table renderer
  // ------------------------------
  const recOpts = ['0', 'N', 'P']; // 0-Unknown, N-Negative, P-Positive

  const renderReceptorTable = () => (
    <Table size="small" sx={{ mt: 1 }}>
      <TableHead>
        <TableRow>
          <TableCell>Receptor</TableCell>
          {['Unknown', 'Negative', 'Positive'].map((t) => (
            <TableCell key={t} align="center">
              {t}
            </TableCell>
          ))}
        </TableRow>
      </TableHead>

      <TableBody>
        {receptors.map((r) => {
          const val = cur.receptorStatus?.[r] ?? '0';
          const setVal = (v) =>
            updateCurrent({
              receptorStatus: { ...cur.receptorStatus, [r]: v }
            });

          return (
            <TableRow key={r}>
              <TableCell>{r}</TableCell>
              {recOpts.map((v) => (
                <TableCell key={v} align="center">
                  <Radio
                    size="small"
                    checked={val === v}
                    onChange={() => setVal(v)}
                  />
                </TableCell>
              ))}
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
  // genotype selector (test type + result)
  // ------------------------------
  // Genotype table renderer
  // ------------------------------
  const typeOpts = ['0', 'S', 'T']; // 0-Unknown, S-Mutation, T-Direct
  const resultOpts = ['0', 'P', 'N']; // 0-Unknown, P-Positive, N-Negative

  const renderGenotypeTable = () => (
    <Table size="small">
      {/* 表头 */}
      <TableHead>
        <TableRow>
          <TableCell rowSpan={2}>Gene&nbsp;Test</TableCell>
          <TableCell align="center" colSpan={3}>
            Type
          </TableCell>
          <TableCell align="center" colSpan={3}>
            Result
          </TableCell>
        </TableRow>
        <TableRow>
          {[
            'Unknown',
            'Mutation Search',
            'Direct Gene',
            'Unknown',
            'Positive',
            'Negative'
          ].map((txt) => (
            <TableCell key={txt} align="center">
              {txt}
            </TableCell>
          ))}
        </TableRow>
      </TableHead>

      {/* 表体 */}
      <TableBody>
        {genes.map((gene) => {
          const g = cur.genotype?.[gene] ?? { type: '0', result: '0' };

          const setType = (val) =>
            updateCurrent({
              genotype: { ...cur.genotype, [gene]: { ...g, type: val } }
            });
          const setResult = (val) =>
            updateCurrent({
              genotype: { ...cur.genotype, [gene]: { ...g, result: val } }
            });

          return (
            <TableRow key={gene}>
              <TableCell>{gene}&nbsp;Test</TableCell>

              {/* type radios */}
              {typeOpts.map((v) => (
                <TableCell key={v} align="center">
                  <Radio
                    size="small"
                    checked={g.type === v}
                    onChange={() => setType(v)}
                  />
                </TableCell>
              ))}

              {/* result radios */}
              {resultOpts.map((v) => (
                <TableCell key={v} align="center">
                  <Radio
                    size="small"
                    checked={g.result === v}
                    onChange={() => setResult(v)}
                  />
                </TableCell>
              ))}
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );

  const resolveTargetId = () =>
    data?.data?.familyMemberId ||
    data?.data?.proBandId ||
    data?.familyMemberId ||
    data?.proBandId ||
    cur.familyMemberId;

  const getTargetMember = () => {
    const targetId = resolveTargetId();
    return (
      pages.find(
        (member) => String(member.familyMemberId) === String(targetId)
      ) || cur
    );
  };

  const handleDownload = () => {
    const targetId = resolveTargetId();
    onDownload?.(pages, targetId);
    onClose();
    setSnack({
      status: true,
      msg: 'Downloading…',
      severity: 'info'
    });
  };

  const handleCalculate = () => {
    const targetMember = getTargetMember();
    if (!targetMember?.hasDob) {
      setSnack({
        status: true,
        msg: 'Enter a date of birth for the target person to run risk calculation.',
        severity: 'error'
      });
      return;
    }

    const targetId = resolveTargetId();
    if (!userId) {
      setSnack({
        status: true,
        msg: 'Please save the pedigree first',
        severity: 'error'
      });
      return;
    }

    setScoreResult(null);
    setScoreError('');
    setCalculating(true);
    setSnack({
      status: true,
      msg: 'Calculating…',
      severity: 'info'
    });

    axios
      .post(APICONSTANTS.calculateCanRiskScore.replace(':user_id', userId), {
        members: pages,
        targetId
      })
      .then((res) => {
        setScoreError('');
        setScoreResult(res.data);
      })
      .catch((err) => {
        const errorMessage =
          err?.response?.data?.error ||
          err?.response?.data?.['Model Error'] ||
          err?.response?.data?.['Pedigree Error'] ||
          'Error calculating CanRisk scores';
        setScoreResult(null);
        setScoreError(errorMessage);
      })
      .finally(() => {
        setCalculating(false);
      });
  };

  const pedigreeResult = scoreResult?.pedigree_result?.[0];
  const tenYearRisk = pedigreeResult?.ten_yr_cancer_risk?.[0];
  const lifetimeRisk = pedigreeResult?.lifetime_cancer_risk?.[0];
  const mutationProbabilities = Array.isArray(
    pedigreeResult?.mutation_probabilties
  )
    ? pedigreeResult.mutation_probabilties
    : [];

  // ---------------------------------------
  //          Render modal
  // ---------------------------------------

  return (
    <>
      <Modal open={open} onClose={onClose}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 700,
            maxHeight: 700,
            bgcolor:
              theme.appearance.value === 'light'
                ? 'white'
                : theme.appearance.shade,
            borderRadius: 2,
            boxShadow: 24,

            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          }}
        >
          <Box
            sx={{
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              p: 2,
              backgroundColor: 'secondary.main',
              borderTopLeftRadius: 8,
              borderTopRightRadius: 8
            }}
          >
            <Typography variant="h6" sx={{ color: '#fff' }}>
              CanRisk – {cur.display || 'Unknown'}
            </Typography>
            <IconButton
              onClick={onClose}
              size="small"
              aria-label="Close modal"
              sx={{ color: '#fff' }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          <Box sx={{ p: 3, flexGrow: 1, overflowY: 'auto' }}>
            {/* Individual Data (read-only) from the saved Risk Factor record */}
            {individualDataRows.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Individual Data (from Risk Factor record)
                </Typography>
                <Box
                  sx={{
                    border: '0.5px solid',
                    borderColor: 'divider',
                    borderRadius: '8px',
                    p: 1.5
                  }}
                >
                  {individualDataRows.map(([label, value]) => (
                    <Box
                      key={label}
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        py: 0.25
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          color: 'text.secondary'
                        }}
                      >
                        {label}
                      </Typography>
                      <Typography variant="body2">{String(value)}</Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}

            {/* Ashkenazi */}
            <Typography variant="subtitle2" gutterBottom>
              Ashkenazi heritage
            </Typography>
            <RadioGroup
              row
              value={cur.ashkenazi ?? 'u'}
              onChange={(e) => updateCurrent({ ashkenazi: e.target.value })}
            >
              <FormControlLabel value="u" control={<Radio />} label="Unknown" />
              <FormControlLabel value="1" control={<Radio />} label="Yes" />
              <FormControlLabel value="0" control={<Radio />} label="No" />
            </RadioGroup>

            {/* Ages */}
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle2" gutterBottom>
                Cancer diagnosis ages
              </Typography>
              <Grid container spacing={2}>
                {cancerSites.map((site) => (
                  <Grid key={site.key} size={6}>
                    <TextField
                      label={site.label}
                      size="small"
                      fullWidth
                      value={cur.ages?.[site.key] ?? ''}
                      onChange={(e) =>
                        updateCurrent({
                          ages: { ...cur.ages, [site.key]: e.target.value }
                        })
                      }
                    />
                  </Grid>
                ))}
              </Grid>
            </Box>

            {/* Receptors */}
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle2" gutterBottom>
                Receptor status
              </Typography>
              {renderReceptorTable()}
            </Box>

            {/* Genotypes */}
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle2" gutterBottom>
                Genetic test results
              </Typography>
              {renderGenotypeTable()}
            </Box>
          </Box>

          <Box
            sx={{
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              p: 2,
              borderTop: 1,
              borderColor: 'divider'
            }}
          >
            <Box>
              <Button
                disabled={pageIdx === 0}
                onClick={() => setPageIdx((i) => i - 1)}
              >
                ←
              </Button>
              <Typography variant="caption" component="span" sx={{ mx: 1 }}>
                {pageIdx + 1} / {pages.length}
              </Typography>
              <Button
                disabled={pageIdx === pages.length - 1}
                onClick={() => setPageIdx((i) => i + 1)}
              >
                →
              </Button>
              <Button
                onClick={() => {
                  const idx = pages.findIndex((p) => {
                    const m = (p.display || '').match(/\((\d+)\)$/);
                    return m && m[1] === jumpUPN;
                  });
                  if (idx !== -1) setPageIdx(idx);
                }}
                disabled={!jumpUPN}
                sx={{ ml: 1 }}
              >
                Go to
              </Button>
              <TextField
                size="small"
                placeholder="NO"
                value={jumpUPN}
                onChange={(e) => setJumpUPN(e.target.value)}
                sx={{ width: 60 }}
              />
            </Box>

            <Box>
              <Button onClick={onClose} variant="outlined" sx={{ mr: 1 }}>
                Cancel
              </Button>
              <Button
                onClick={handleDownload}
                variant="contained"
                color="secondary"
                sx={{ mr: 1 }}
              >
                Download
              </Button>
              <Button
                onClick={handleCalculate}
                variant="contained"
                color="secondary"
                disabled={!canRiskConfigured || canRiskChecking || calculating}
              >
                {calculating ? 'Waiting…' : 'Get Score'}
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>
      <Modal open={Boolean(scoreResult)} onClose={() => setScoreResult(null)}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 760,
            maxHeight: 720,
            bgcolor:
              theme.appearance.value === 'light'
                ? 'white'
                : theme.appearance.shade,
            borderRadius: 2,
            boxShadow: 24,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          }}
        >
          <Box
            sx={{
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              p: 2,
              backgroundColor: 'secondary.main',
              borderTopLeftRadius: 8,
              borderTopRightRadius: 8
            }}
          >
            <Typography variant="h6" sx={{ color: '#fff' }}>
              CanRisk Score Results
            </Typography>
            <IconButton
              onClick={() => setScoreResult(null)}
              size="small"
              aria-label="Close modal"
              sx={{ color: '#fff' }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          <Box sx={{ p: 3, flexGrow: 1, overflowY: 'auto' }}>
            <Typography variant="body2" gutterBottom>
              Version: {scoreResult?.version || '-'}
            </Typography>
            <Typography variant="body2" gutterBottom>
              Calculated at: {scoreResult?.timestamp || '-'}
            </Typography>

            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Risk summary
              </Typography>
              <Typography variant="body2">
                10-year breast cancer risk:{' '}
                {tenYearRisk?.['breast cancer risk']?.percent ?? '-'}%
              </Typography>
              <Typography variant="body2">
                Lifetime breast cancer risk:{' '}
                {lifetimeRisk?.['breast cancer risk']?.percent ?? '-'}%
              </Typography>
            </Box>

            {Array.isArray(scoreResult?.warnings) &&
              scoreResult.warnings.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Warnings
                  </Typography>
                  {scoreResult.warnings.map((warning, idx) => (
                    <Typography variant="body2" key={`${warning}-${idx}`}>
                      - {warning}
                    </Typography>
                  ))}
                </Box>
              )}

            {mutationProbabilities.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Mutation probabilities
                </Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Gene</TableCell>
                      <TableCell align="right">Percent</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {mutationProbabilities.map((entry, idx) => {
                      const [gene, value] =
                        Object.entries(entry || {})[0] || [];
                      return (
                        <TableRow key={`${gene || 'row'}-${idx}`}>
                          <TableCell>{gene || '-'}</TableCell>
                          <TableCell align="right">
                            {value?.percent ?? '-'}%
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </Box>
            )}

            <Box
              sx={{
                mt: 2,
                p: 2,
                borderRadius: 1,
                maxHeight: 260,
                overflowY: 'auto',
                bgcolor:
                  theme.appearance.value === 'light'
                    ? '#f5f5f5'
                    : 'rgba(255,255,255,0.08)'
              }}
            >
              <pre
                style={{
                  margin: 0,
                  fontSize: 12,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word'
                }}
              >
                {JSON.stringify(scoreResult, null, 2)}
              </pre>
            </Box>
          </Box>

          <Box
            sx={{
              p: 2,
              borderTop: 1,
              borderColor: 'divider',
              display: 'flex',
              justifyContent: 'flex-end'
            }}
          >
            <Button onClick={() => setScoreResult(null)} variant="contained">
              Close
            </Button>
          </Box>
        </Box>
      </Modal>
      <Modal open={Boolean(scoreError)} onClose={() => setScoreError('')}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 760,
            maxHeight: 520,
            bgcolor:
              theme.appearance.value === 'light'
                ? 'white'
                : theme.appearance.shade,
            borderRadius: 2,
            boxShadow: 24,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          }}
        >
          <Box
            sx={{
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              p: 2,
              backgroundColor: 'secondary.main',
              borderTopLeftRadius: 8,
              borderTopRightRadius: 8
            }}
          >
            <Typography variant="h6" sx={{ color: '#fff' }}>
              CanRisk Calculation Error
            </Typography>
            <IconButton
              onClick={() => setScoreError('')}
              size="small"
              aria-label="Close modal"
              sx={{ color: '#fff' }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          <Box sx={{ p: 3, flexGrow: 1, overflowY: 'auto' }}>
            <Typography
              variant="body2"
              sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
            >
              {scoreError}
            </Typography>
          </Box>

          <Box
            sx={{
              p: 2,
              borderTop: 1,
              borderColor: 'divider',
              display: 'flex',
              justifyContent: 'flex-end'
            }}
          >
            <Button onClick={() => setScoreError('')} variant="contained">
              Close
            </Button>
          </Box>
        </Box>
      </Modal>
    </>
  );
}
